<template>
  <v-btn
    v-if="props.showOrgSwitchButton"
    :disabled="!hasMultipleOrgs"
    color="surface"
    max-width="173"
    :title="selectedOrg?.name ?? '-'"
    @click="dialog = true"
  >
    <slot name="modal-visibility-btn-content">
      <span class="text-truncate" style="max-width: 173px">
        {{ selectedOrg?.name ?? '-' }}
      </span>
    </slot></v-btn
  >
  <v-dialog v-model="dialog" max-width="1200">
    <v-card title="Organisation auswählen">
      <v-card-text>
        <v-text-field
          v-model="searchQuery"
          label="Organisation suchen"
          prepend-inner-icon="mdi-magnify"
          single-line
          hide-details
        />
        <v-data-table
          v-if="context.organisations.objects.length > 1"
          v-model:items-per-page="itemsPerPage"
          :headers="columns"
          :items="filteredOrganisations"
          item-value="name"
          items-per-page-text="Elemente pro Seite"
          class="elevation-1"
        >
          <template #item.name="{ item }">
            <v-btn
              prepend-icon="mdi-account-group"
              color="primary"
              variant="outlined"
              :text="item.name"
              @click="selectOrg(item)"
            />
          </template>
          <template #item.favorite="{ item }">
            <v-btn
              icon="mdi-star"
              :color="item.favorite ? 'primary' : 'grey-lighten-1'"
              variant="text"
              @click="updateFavorites(item.id, !item.favorite)"
            />
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          prepend-icon="mdi-close"
          text="schliessen"
          @click="dialog = false"
        ></v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import debounce from 'lodash.debounce'
import { computed, onMounted, Ref, ref, watch } from 'vue'
import { useStore } from 'vuex'

import { apiClientV2 } from '@/api/ApiClientV2'
import { Organisation } from '@/models/core/organisation'
import { Profile } from '@/models/core/profile'

const context = computed(() => store.state.global.context)
const store = useStore()
const dialog = ref(false)

type OrganistationSelectionProps = {
  showOrgSwitchButton?: boolean
  initialOrgId?: string
}

const props = withDefaults(defineProps<OrganistationSelectionProps>(), {
  showOrgSwitchButton: true,
  initialOrgId: null,
})

const selectedOrg: Ref<Organisation> = ref(null)
const hasMultipleOrgs = computed(
  () => context.value.organisations.objects.length > 1
)
const itemsPerPage = ref(10)
const searchQuery = ref('')

const emit = defineEmits(['input', 'selected'])

const columns = [
  {
    key: 'name',
    title: 'Name',
    searchable: true,
  },
  {
    key: 'meta.address',
    title: 'Adresse',
    searchable: true,
  },
  {
    key: 'meta.city',
    title: 'Ort',
    searchable: true,
  },
  {
    key: 'favorite',
    title: 'Favorit',
    searchable: false,
  },
]

const selectOrg = (item) => {
  // TODO: Why sometimes item.item and sometimes item?
  if (item?.item) item = item.item?.raw
  selectedOrg.value = item
  emit('selected', selectedOrg.value)
  close()
}

const clearSearchQuery = () => {
  debounce(() => {
    searchQuery.value = ''
  }, 300)()
}

const close = () => {
  dialog.value = false
  emit('input', false)
  clearSearchQuery()
}

const filteredOrganisations = computed(() => {
  const query = searchQuery.value.toLowerCase()
  return (
    context.value.organisations.objects
      // Filter for organisations that match the search query
      .filter((org) => org.name.toLowerCase().includes(query))
      // Map the "favorite" property from the users profile meta data to the organisation
      .map((org) => {
        org.favorite = false
        store.state.global.profile.meta.favorite_organisations?.forEach(
          (favOrgId) => {
            if (favOrgId === org.id) {
              org.favorite = true
            }
          }
        )
        return org
      })
      // sort the organisations by favorite status and alphabetic by name
      .sort((a, b) => {
        if (a.favorite && !b.favorite) return -1
        if (!a.favorite && b.favorite) return 1
        return a.name.localeCompare(b.name)
      })
  )
})

const updateFavorites = async (orgId, isFavorite) => {
  const profile = store.state.global.profile
  if (!profile.meta.favorite_organisations) {
    profile.meta.favorite_organisations = []
  }
  if (isFavorite) {
    profile.meta.favorite_organisations.indexOf(orgId) === -1 &&
      profile.meta.favorite_organisations.push(orgId)
  } else {
    profile.meta.favorite_organisations.indexOf(orgId) !== -1 &&
      profile.meta.favorite_organisations.splice(
        profile.meta.favorite_organisations.indexOf(orgId),
        1
      )
  }
  await apiClientV2.update<Profile, Profile>(Profile, profile)
}

watch(dialog, (val) => {
  if (val === false) {
    clearSearchQuery()
  }
})

onMounted(() => {
  if (props.initialOrgId && context.value.organisations.objects.length > 0) {
    const org = context.value.organisations.objects.find(
      (o) => o.id === props.initialOrgId
    )
    selectOrg(org)
  }
})
</script>
